import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BarChart3, Settings2 } from "lucide-react";

const WEEKDAYS = [
  { idx: 0, short: "Mon" }, { idx: 1, short: "Tue" }, { idx: 2, short: "Wed" },
  { idx: 3, short: "Thu" }, { idx: 4, short: "Fri" }, { idx: 5, short: "Sat" }, { idx: 6, short: "Sun" },
];

const fmtH = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
};

export default function Utilisation() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/utilisation?year=${year}&month=${month}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const openSettings = async () => {
    try {
      const { data } = await api.get("/admin/org-settings");
      setSettings(data);
      setSettingsOpen(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveSettings = async () => {
    try {
      const { data } = await api.patch("/admin/org-settings", {
        daily_hours: settings.daily_hours,
        workweek: settings.workweek,
        office_start: settings.office_start,
        office_end: settings.office_end,
        auto_logout: settings.auto_logout,
        late_extension_minutes: settings.late_extension_minutes,
      });
      setSettings(data);
      toast.success("Org settings saved");
      setSettingsOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const monthOptions = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Report</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Utilisation</h1>
          <p className="text-sm text-slate-500 mt-1">Billable hours vs available hours for each teammate this month.</p>
        </div>
        <Button data-testid="util-settings-btn" variant="outline" onClick={openSettings} className="gap-2">
          <Settings2 className="w-4 h-4" /> Org settings
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Month</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
            <SelectTrigger data-testid="util-month" className="mt-1 w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Year</Label>
          <Input data-testid="util-year" type="number" value={year} min="2020" max="2100" onChange={(e) => setYear(parseInt(e.target.value, 10) || year)} className="mt-1 mono w-[100px]" />
        </div>
        {data && (
          <div className="ml-auto text-xs text-slate-600">
            <span className="uppercase tracking-wider text-slate-500">Working days:</span> <b className="mono">{data.working_days}</b>
            <span className="mx-2 text-slate-300">·</span>
            <span className="uppercase tracking-wider text-slate-500">Available:</span> <b className="mono">{fmtH(data.available_seconds)}</b>
            <span className="mx-2 text-slate-300">·</span>
            <span className="uppercase tracking-wider text-slate-500">Daily:</span> <b className="mono">{data.daily_hours}h</b>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> By teammate
        </div>
        <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
          <div className="col-span-3">User</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2 text-right">Billable</div>
          <div className="col-span-2 text-right">Available</div>
          <div className="col-span-1 text-right">Idle</div>
          <div className="col-span-2 text-right">Utilisation %</div>
        </div>
        {loading ? <div className="p-8 text-center text-sm text-slate-500">Loading…</div> :
         !data?.rows?.length ? <div className="p-8 text-center text-sm text-slate-500">No teammates yet.</div> : (
          <ul className="divide-y divide-slate-200">
            {data.rows.map((r) => {
              const pct = r.utilisation_pct;
              const pctColor = pct >= 75 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                : pct >= 40 ? "text-amber-800 bg-amber-50 border-amber-200"
                : "text-rose-700 bg-rose-50 border-rose-200";
              return (
                <li key={r.user_id} data-testid={`util-row-${r.user_id}`} className="grid grid-cols-12 gap-2 px-5 py-3 items-center">
                  <div className="col-span-3 font-medium truncate">{r.user_name}</div>
                  <div className="col-span-2 text-xs text-slate-500 capitalize">{r.role.replace("_", " ")}</div>
                  <div className="col-span-2 text-right mono text-sm font-medium">{fmtH(r.billable_seconds)}</div>
                  <div className="col-span-2 text-right mono text-sm text-slate-500">{fmtH(r.available_seconds)}</div>
                  <div className="col-span-1 text-right mono text-xs text-slate-500">{fmtH(r.idle_seconds)}</div>
                  <div className="col-span-2 text-right">
                    <span className={`inline-block text-xs mono px-2 py-0.5 rounded-sm border ${pctColor}`} data-testid={`util-pct-${r.user_id}`}>{pct}%</span>
                  </div>
                </li>
              );
            })}
          </ul>
         )}
      </div>

      {/* Org settings modal */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="org-settings-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Org working-time settings</DialogTitle></DialogHeader>
          {settings && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Daily hours</Label>
                <Input
                  data-testid="org-daily-hours"
                  type="number" step="0.5" min="0.5" max="24"
                  value={settings.daily_hours}
                  onChange={(e) => setSettings({ ...settings, daily_hours: parseFloat(e.target.value) || 0 })}
                  className="mt-1 mono w-[120px]"
                />
                <p className="text-[11px] text-slate-500 mt-1">Standard working hours per day for utilisation math.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Office hours</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    data-testid="org-office-start"
                    type="time"
                    value={settings.office_start || "09:30"}
                    onChange={(e) => setSettings({ ...settings, office_start: e.target.value })}
                    className="mono w-[130px]"
                  />
                  <span className="text-slate-400">to</span>
                  <Input
                    data-testid="org-office-end"
                    type="time"
                    value={settings.office_end || "18:30"}
                    onChange={(e) => setSettings({ ...settings, office_end: e.target.value })}
                    className="mono w-[130px]"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Idle time is only counted between these hours on workweek days.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">End-of-day logout</Label>
                <label className="mt-2 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input data-testid="org-auto-logout" type="checkbox" className="accent-blue-700 w-4 h-4" checked={settings.auto_logout !== false}
                    onChange={(e) => setSettings({ ...settings, auto_logout: e.target.checked })} />
                  Sign everyone out at office end time, every day
                </label>
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                  <span>After-hours sign-in lasts</span>
                  <Input data-testid="org-late-extension" type="number" min="15" max="720" step="15" disabled={settings.auto_logout === false}
                    value={settings.late_extension_minutes ?? 60} className="mono w-[90px]"
                    onChange={(e) => setSettings({ ...settings, late_extension_minutes: parseInt(e.target.value, 10) || 0 })} />
                  <span>minutes</span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Users get a warning 10 minutes before. Running timers stop at the end time.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Workweek</Label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((d) => {
                    const active = settings.workweek.includes(d.idx);
                    return (
                      <button
                        key={d.idx}
                        data-testid={`org-day-${d.short.toLowerCase()}`}
                        onClick={() => {
                          const next = active
                            ? settings.workweek.filter((x) => x !== d.idx)
                            : [...settings.workweek, d.idx].sort();
                          setSettings({ ...settings, workweek: next });
                        }}
                        className={`px-3 py-1.5 text-xs rounded-md border transition ${
                          active
                            ? "bg-blue-700 text-white border-blue-700"
                            : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        {d.short}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">Only these days count toward available hours.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button data-testid="org-settings-save" onClick={saveSettings} className="bg-blue-700 hover:bg-blue-800 text-white">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
